import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconPlus, IconTrash } from '../../../common/icons';
import { fmtMoney } from '../../../common/utils/money';
import { formatDateOnly } from '../../../common/utils/date';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useCowLiquidation } from '../contexts/liquidations';
import { useDeleteExchange } from '../contexts/exchanges';
import StaysPanel from '../forms/stays-panel';
import ExchangeForm from '../forms/exchange-form';
import SharesList from './shares';

import '../styles/billing.css';

// Liquidación de UNA vaca (modelo v1 cows/liquidation, adaptado a v2). Workspace en
// tres bloques, como en v1: (1) Manejo/estadías — la fuente de los días de pensión;
// (2) Ajustes/exchanges del período; (3) Resultado read-only del drill-down vaca-mes
// (Q-118: pensión + reparto congelado + neto). A diferencia de v1, Cowre NO calcula:
// los días los deriva el backend de las estadías y los montos vienen del servidor.
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const KIND = { charge: { label: 'Cargo', tone: 'charge' }, credit: { label: 'Haber', tone: 'credit' } };

export default function CowLiquidation({ animalId }) {
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [month, setMonth] = useState(() => new Date().getMonth() + 1);
    const years = useMemo(() => { const y = new Date().getFullYear(); return [y + 1, y, y - 1, y - 2]; }, []);
    const [adding, setAdding] = useState(false);

    const { data, isLoading, isFetching, error } = useCowLiquidation(animalId, year, month);
    const { mutateAsync: removeExchange, isPending: removing } = useDeleteExchange();
    const confirm = useConfirm();

    const animal = data?.animal;
    const lines = data?.liquidations || [];
    const exchanges = data?.exchanges || [];
    const sub = data?.subtotal || {};
    const label = animal
        ? `${animal.name || animal.number || `#${animalId}`}${animal.number && animal.name ? ` · ${animal.number}` : ''}`
        : `#${animalId}`;

    const delExchange = async (x) => {
        const ok = await confirm({
            title: 'Eliminar ajuste',
            message: '¿Eliminar este ajuste?',
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try { await removeExchange(x.id); toast.success('Ajuste eliminado'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar'); }
    };

    return (
        <div className={`billing-liq ${isFetching ? 'billing-liq--busy' : ''}`}>
            <header className="billing-liq__header">
                <div>
                    <Link to="/admin/animals" className="billing-liq__back">Animales</Link>
                    <h1 className="billing-liq__title">Liquidación · {label}</h1>
                </div>
                <div className="billing-liq__period">
                    <select value={month} onChange={(e) => setMonth(Number(e.target.value))} disabled={isFetching}>
                        {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))} disabled={isFetching}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </header>

            {error && <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver la liquidación.' : (error.message || 'No se pudo cargar la liquidación')}</div>}

            {/* 1 · Manejo (estadías) — fuente de los días. Alta/cambio/salida/historial. */}
            <StaysPanel animalId={animalId} />

            {/* 2 · Ajustes (exchanges) del período seleccionado. */}
            <section className="cat-detail__card">
                <div className="billing-liq__section-head">
                    <h2 className="cat-detail__section-title">Ajustes de {MONTHS[month - 1]} {year}</h2>
                    <Gate domain="billing" action="write">
                        <button type="button" className="billing-btn billing-btn--primary billing-btn--sm" onClick={() => setAdding(true)}>
                            <IconPlus /> <span>Agregar ajuste</span>
                        </button>
                    </Gate>
                </div>

                {isLoading && !data ? (
                    <p className="billing-modal__state">Cargando…</p>
                ) : !exchanges.length ? (
                    <p className="billing-stays__none">Sin ajustes en este período. Un cargo suma y un haber (p.ej. leche) resta al neto.</p>
                ) : (
                    <table className="cat-table billing-lines">
                        <thead>
                            <tr>
                                <th className="billing-lines__left">Concepto</th>
                                <th className="billing-lines__left">Fecha</th>
                                <th>Tipo</th>
                                <th className="billing-lines__num">Monto</th>
                                <th aria-label="Acciones" />
                            </tr>
                        </thead>
                        <tbody>
                            {exchanges.map((x) => (
                                <tr key={x.id} className="cat-table__row">
                                    <td className="billing-lines__left">{x.event || x.description || '—'}</td>
                                    <td className="billing-lines__left">{formatDateOnly(x.event_date) || '—'}</td>
                                    <td><span className={`billing-kind billing-kind--${KIND[x.kind]?.tone || 'charge'}`}>{KIND[x.kind]?.label || x.kind}</span></td>
                                    <td className="billing-lines__num billing-lines__strong">{x.kind === 'credit' ? '−' : ''}{fmtMoney(x.amount)}</td>
                                    <td>
                                        <Gate domain="billing" action="write">
                                            <button type="button" className="billing-icon billing-icon--danger" onClick={() => delExchange(x)} disabled={removing} aria-label="Eliminar ajuste" title="Eliminar"><IconTrash /></button>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* 3 · Resultado del período (drill-down Q-118) — read-only. */}
            <section className="cat-detail__card">
                <h2 className="cat-detail__section-title">Resultado de {MONTHS[month - 1]} {year}</h2>

                <div className="billing-liq__total-card">
                    <span className="billing-liq__total-label">Neto del período</span>
                    <span className="billing-liq__total-value">{fmtMoney(sub.net)}</span>
                    <span className="billing-liq__total-break">
                        Pensión {fmtMoney(sub.pension_total)} · Cargos {fmtMoney(sub.charge_total)} · Haberes −{fmtMoney(sub.credit_total)}
                    </span>
                </div>

                {isLoading && !data ? (
                    <p className="billing-modal__state">Cargando…</p>
                ) : !lines.length ? (
                    <p className="billing-liq__hint">Aún no hay pensión generada para {MONTHS[month - 1]} {year}. La pensión se emite en la corrida mensual (Facturación → Liquidaciones); acá registrás el manejo y los ajustes que la alimentan.</p>
                ) : (
                    <table className="cat-table billing-lines">
                        <thead>
                            <tr>
                                <th className="billing-lines__left">Manejo</th>
                                <th className="billing-lines__num">Días</th>
                                <th className="billing-lines__num">Tarifa/día</th>
                                <th className="billing-lines__num">Pensión</th>
                                <th className="billing-lines__left billing-lines__shares-col">Reparto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((l) => (
                                <tr key={l.id} className="cat-table__row">
                                    <td className="billing-lines__left">{l.management?.name || '—'}</td>
                                    <td className="billing-lines__num">{l.days ?? '—'}</td>
                                    <td className="billing-lines__num">{fmtMoney(l.day_rate_snapshot)}</td>
                                    <td className="billing-lines__num billing-lines__strong">{fmtMoney(l.pension_amount)}</td>
                                    <td className="billing-lines__left billing-lines__shares-col"><SharesList shares={l.shares} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {adding && <ExchangeForm animalId={animalId} year={year} month={month} onClose={() => setAdding(false)} onSaved={() => setAdding(false)} />}
        </div>
    );
}
